import { DeviceFilterState } from "@/components/devices/DeviceFilters";

export interface DeviceAssignment {
  id: string;
  device_id: string;
  user_id: string;
  organization_id?: string | null;
  organization_name?: string | null;
  device_details?: {
    device_last_updated: string | null;
    device_name?: string | null;
    device_serial?: string | null;
    device_description?: string | null;
    group_name?: string | null;
    group_color?: string | null;
    notification?: {
      result?: string;
      message?: string;
    } | null;
    location?: {
      name?: string;
    } | null;
    lat_wgs84?: number | null;
    long_wgs84?: number | null;
    device_status?: string | null;
    imagekit?: {
      thumbnailUrl?: string;
    } | null;
  };
}

export const filterAndSortDevices = (
  devices: DeviceAssignment[],
  filters: DeviceFilterState
): DeviceAssignment[] => {
  let filtered = [...devices];

  // Apply search filter
  if (filters.search.trim()) {
    const searchTerm = filters.search.toLowerCase().trim();
    filtered = filtered.filter((device) => {
      const id = device.device_id?.toLowerCase() || "";
      const serial = device.device_details?.device_serial?.toLowerCase() || "";
      const name = device.device_details?.device_name?.toLowerCase() || "";
      const description = device.device_details?.device_description?.toLowerCase() || "";
      const orgName = device.organization_name?.toLowerCase() || "";
      return id.includes(searchTerm) || serial.includes(searchTerm) || name.includes(searchTerm) || description.includes(searchTerm) || orgName.includes(searchTerm);
    });
  }

  // Apply alerts filter
  if (filters.showAlertsOnly) {
    filtered = filtered.filter((device) => {
      return device.device_details?.notification?.result === "true";
    });
  }

  // Apply offline filter
  if (filters.showOfflineOnly) {
    filtered = filtered.filter((device) => {
      const lastUpdated = device.device_details?.device_last_updated;
      if (!lastUpdated) return true;
      
      const now = new Date();
      const deviceTime = new Date(lastUpdated);
      const hoursDiff = (now.getTime() - deviceTime.getTime()) / (1000 * 60 * 60);
      
      return hoursDiff > 24; // Consider offline if no update in 24 hours
    });
  }

  // Apply demo filter
  if (filters.showDemoOnly) {
    filtered = filtered.filter((device) => {
      return device.device_details?.device_status?.toLowerCase() === "demo";
    });
  }

  // Apply group filter
  if (filters.groupName) {
    filtered = filtered.filter((device) => {
      const groupName = device.device_details?.group_name?.toLowerCase() || "";
      return groupName === filters.groupName.toLowerCase();
    });
  }

  // Apply sorting
  filtered.sort((a, b) => {
    let comparison = 0;
    
    switch (filters.sortBy) {
      case "name":
        const nameA = a.device_details?.device_name || "";
        const nameB = b.device_details?.device_name || "";
        comparison = nameA.localeCompare(nameB);
        break;
        
      case "recent":
        const dateA = a.device_details?.device_last_updated ? new Date(a.device_details.device_last_updated) : new Date(0);
        const dateB = b.device_details?.device_last_updated ? new Date(b.device_details.device_last_updated) : new Date(0);
        comparison = dateB.getTime() - dateA.getTime(); // Most recent first by default
        break;
        
      case "alerts":
        const alertA = a.device_details?.notification?.result === "true" ? 1 : 0;
        const alertB = b.device_details?.notification?.result === "true" ? 1 : 0;
        comparison = alertB - alertA; // Alerts first
        break;
        
      case "location":
        const locationA = a.device_details?.location?.name || "";
        const locationB = b.device_details?.location?.name || "";
        comparison = locationA.localeCompare(locationB);
        break;
        
      default:
        return 0;
    }
    
    // Apply sort direction (only for name and location, others have natural direction)
    if (filters.sortBy === "name" || filters.sortBy === "location") {
      return filters.sortDirection === "desc" ? -comparison : comparison;
    }
    
    return comparison;
  });

  return filtered;
};

export const highlightSearchTerm = (text: string, searchTerm: string): string => {
  if (!searchTerm.trim()) return text;
  
  const regex = new RegExp(`(${searchTerm.trim()})`, 'gi');
  return text.replace(regex, '<mark class="bg-yellow-200 dark:bg-yellow-800">$1</mark>');
};